import { useCallback, useEffect, useState } from 'react';
import { api, getToken, setToken, type QuarantineItem, type ServerConfig } from '../shared/api.ts';
import { Quarantine } from './Quarantine.tsx';
import { Library } from './Library.tsx';
import { Push } from './Push.tsx';
import { Settings } from './Settings.tsx';
import { PairGate } from './PairGate.tsx';
import { useSocket } from './useSocket.ts';
import { registerServiceWorker, setBadge } from './push.ts';
import './app.css';

type Tab = 'inbox' | 'library' | 'push' | 'settings';

const TABS: { id: Tab; label: string }[] = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'library', label: 'Library' },
  { id: 'push', label: 'Push' },
  { id: 'settings', label: 'Settings' },
];

export function App(): React.JSX.Element {
  const [authed, setAuthed] = useState(getToken() !== null);
  const [tab, setTab] = useState<Tab>('inbox');
  const [config, setConfig] = useState<ServerConfig | null>(null);
  const [items, setItems] = useState<QuarantineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [libraryKey, setLibraryKey] = useState(0);

  useEffect(() => {
    void registerServiceWorker();
    api.config().then(setConfig).catch(() => undefined);
  }, []);

  const loadQuarantine = useCallback(async () => {
    if (!getToken()) return;
    setLoading(true);
    try {
      const data = await api.quarantine();
      setItems(data.items);
      setBadge(data.items.length);
    } catch (err) {
      if (err && typeof err === 'object' && 'status' in err && err.status === 401) {
        setToken(null);
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) void loadQuarantine();
  }, [authed, loadQuarantine]);

  useSocket(
    authed,
    useCallback(
      (event) => {
        if (event.type === 'quarantine.new' || event.type === 'quarantine.resolved') {
          void loadQuarantine();
        }
        if (event.type === 'library.changed') setLibraryKey((k) => k + 1);
        if (event.type === 'hello') setBadge(event.pendingCount);
      },
      [loadQuarantine],
    ),
  );

  // A notification tap lands on /app?transfer=… — go to the inbox and refresh.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && authed) void loadQuarantine();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [authed, loadQuarantine]);

  if (!authed) {
    return <PairGate onPaired={() => setAuthed(true)} />;
  }

  return (
    <div className="app">
      <header className="app__bar">
        <nav className="app__nav">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`app__tab${tab === entry.id ? ' is-current' : ''}`}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
              {entry.id === 'inbox' && items.length > 0 ? (
                <span className="app__count mono">{items.length}</span>
              ) : null}
            </button>
          ))}
        </nav>
      </header>

      <div className="app__body">
        {tab === 'inbox' ? (
          <Quarantine items={items} loading={loading} onChanged={loadQuarantine} />
        ) : null}
        {tab === 'library' ? (
          <Library selection={selection} onSelectionChange={setSelection} refreshKey={libraryKey} />
        ) : null}
        {tab === 'push' ? (
          <Push selection={selection} onSelectionChange={setSelection} refreshKey={libraryKey} />
        ) : null}
        {tab === 'settings' ? <Settings config={config} /> : null}
      </div>
    </div>
  );
}
